/**
 * The day route map: one day's stops as numbered pins, and — when the routing provider answered —
 * the road between them as a single polyline.
 *
 * The degraded case is the reason this component exists at all: `geometry === null` means the
 * provider did not answer (spec §18 forbids showing a distance it did not return), and the map then
 * draws the pins **with no line at all**. Connecting the stops with straight lines is not a fallback —
 * measured against real OSRM output, road distance is 2.24× the straight line, so a drawn straight
 * line asserts a journey the user never drives.
 *
 * The Leaflet loading, cleanup and redraw pattern is `PlaceMap`'s: the library rides a lazy chunk
 * with its stylesheet, the map is created once and removed on unmount, and only the overlay layer is
 * rebuilt when the props change, because the parent switches between days while this component stays
 * mounted and recreating the map would flash tiles on every day switch.
 */

import { useEffect, useRef, useState } from "react";
import type { LayerGroup as LeafletLayerGroup, Map as LeafletMap } from "leaflet";
import type { ReactElement } from "react";

import { decodePolyline } from "@trippool/core";

import { copy } from "../copy.ts";
import { readToken } from "../lib/tokens.ts";

export interface RouteMapStop {
  /** Stable key for React and for the pin's number. */
  id: string;
  /** Shown in a popup when a pin is tapped. */
  name: string;
  lat: number;
  lon: number;
  /**
   * Shown inside the pin. 1-based, matching the timeline row the parent renders beside the map, and
   * rendered as given — renumbering here would break the pin↔row correspondence the side-by-side
   * layout exists to provide.
   */
  position: number;
  /**
   * What the pin shows: a visit's number, or an anchor's glyph.
   *
   * Supplied by the caller from one rule (`stopMarkers`) so the pin and the timeline row beside it cannot
   * disagree — and an anchor is never a number, because there is no order to place it in.
   */
  marker?: string;
  /** Estimated time label (e.g. "08:30") displayed in tooltip on hover. */
  timeLabel?: string | null;
}

export interface RouteMapLeg {
  /**
   * Metres for this leg, or null when the provider did not price it.
   *
   * Carried for the row beside the map; the map itself has no use for a number it cannot draw.
   */
  distanceM: number | null;
  /**
   * This leg's own path, in the same encoded format as the day's `geometry`, or null when the provider
   * gave no path for it.
   *
   * The API now returns one of these per leg — stitched from the provider's step geometries — so the map
   * paints each drive in its own colour. A null is a leg with no road: its line is skipped while its pins
   * and its distance still show, rather than connecting its ends with a straight line.
   */
  geometry: string | null;
}

export interface RouteMapProps {
  stops: RouteMapStop[];
  /**
   * The day's road path as an encoded polyline (precision 5, the format OSRM's `geometries=polyline`
   * serves), or null when routing was unavailable. GeoJSON geometry must be converted by the caller;
   * this component decodes one format only, so it cannot be handed the wrong shape silently.
   */
  geometry: string | null;
  /**
   * The day's legs in order, when the caller has them.
   *
   * Present so the sections of a day can be told apart — the product owner's ask — and each API leg now
   * carries its own `geometry`, so that is what is painted. A leg with no path is skipped while its pins
   * are still drawn and its distance is still said in the list; when no leg brings a path, the day falls
   * back to the single line above.
   */
  legs?: ReadonlyArray<RouteMapLeg>;
  /**
   * Whether the legs were measured. Drives the degraded-state banner: when false, the map draws
   * markers only and the note under it says the distances are unknown rather than leaving a lineless
   * map to imply the plan forgot to include them.
   */
  distancesKnown: boolean;
  /**
   * Whether a road is still on its way.
   *
   * A third state, and not the same claim as `distancesKnown: false`. The arrange step asks for a road
   * while the user is still ticking places, so there is a window with pins drawn and no answer yet — and
   * without this the map would state "không xác định" about a road it is in the middle of measuring,
   * contradicting the note the parent is already showing above it.
   */
  routingPending?: boolean;
  /**
   * The `position` of the stop to emphasise, or null.
   *
   * A position rather than a stop id: the pin already prints it, so "the row numbered 3" and "pin 3" are the
   * same statement, and the parent does not have to reproduce this component's pin-key scheme to point at one.
   */
  highlightedPosition?: number | null;
  /** Called when a marker is hovered on the map, to sync with the timeline list. */
  onHoverPosition?: (position: number | null) => void;
  /** Called when a marker is clicked on the map. */
  onSelectPosition?: (position: number) => void;
}

/**
 * The zoom a lone stop opens at — the same reasoning as `PlaceMap`'s `SINGLE_PLACE_ZOOM`: at 15,
 * city-centre stops a few hundred metres apart separate, and a degenerate one-point "fit" would
 * otherwise zoom to the map's maximum.
 */
const SINGLE_STOP_ZOOM = 15;

/**
 * The tightest the fit-to-stops is allowed to get, taken from `PlaceMap`'s `fitAll` prototype
 * measurement: beyond 14 the two outermost stops pin the view so tight that the middle stops crowd
 * the edges of the screen.
 */
const STOP_FIT_MAX_ZOOM = 14;

/**
 * The tightest the fit-to-road is allowed to get, and why it is looser than the stop fit: a road path
 * hugs its own bounds, so an unrestricted fit would zoom until the day's full road no longer fits the
 * viewport and the route's start and end were the only things visible. 16 keeps the whole day on
 * screen while still filling the map.
 */
const ROAD_FIT_MAX_ZOOM = 16;

/**
 * The colours a day's legs are drawn in, in the order they are used.
 *
 * Five hues that exist in the token layer already and mean nothing of their own here — they are not states
 * (`--tp-color-caution` is a warning elsewhere) and a leg is not a state, so a leg is coloured by its place
 * in the day and by nothing else. The list is cycled rather than extended: a sixth token would be a palette
 * decision this component has no business making, and a sixth leg reusing the first colour is a smaller
 * claim than a colour the stylesheet does not define.
 */
const LEG_COLOURS = [
  "--tp-color-accent",
  "--tp-color-tag-blue",
  "--tp-color-tag-warm",
  "--tp-color-caution",
  "--tp-color-restful",
] as const;

export function RouteMap(props: RouteMapProps): ReactElement {
  const {
    stops,
    geometry,
    legs,
    distancesKnown,
    routingPending,
    highlightedPosition,
    onHoverPosition,
    onSelectPosition,
  } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const layerRef = useRef<LeafletLayerGroup | null>(null);
  const leafletRef = useRef<typeof import("leaflet") | null>(null);
  /** The pins by stop position, so a highlight repaints one element instead of rebuilding the day. */
  const pinsRef = useRef<Map<number, import("leaflet").Marker>>(new Map());

  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Same pairing as `PlaceMap` and `MapPicker`: Leaflet's stylesheet rides the same dynamic chunk
    // graph — without it the tile layer renders as a broken grid of unpositioned images.
    void Promise.all([import("leaflet"), import("leaflet/dist/leaflet.css")]).then(([L]) => {
      if (cancelled || containerRef.current === null) return;
      leafletRef.current = L;

      const map = L.map(containerRef.current, { scrollWheelZoom: false });
      mapRef.current = map;

      // Street tiles from OpenStreetMap; the attribution is a licence requirement, not decoration.
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 18,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(map);

      layerRef.current = L.layerGroup().addTo(map);
      setLoaded(true);
    });

    return () => {
      cancelled = true;
      mapRef.current?.remove();
      mapRef.current = null;
      layerRef.current = null;
      leafletRef.current = null;
    };
  }, []);

  // Redraw the whole day — line and pins — and refit the view, whenever the day changes. The parent
  // keeps this component mounted across day switches, so the fit belongs here rather than at map
  // creation: each day's data has bounds of its own, and carrying the previous day's viewport over
  // would show a new day's pins half off screen.
  useEffect(() => {
    const L = leafletRef.current;
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!loaded || L === null || map === null || layer === null) return;

    layer.clearLayers();
    pinsRef.current.clear();

    /*
     * What is painted: one path per leg, otherwise the day's own path.
     *
     * This drew a single line for the whole day, and the argument was the seam: the geometry already joins
     * the legs, so per-leg segments would show a joint wherever two of them meet. The product owner has since
     * asked for the sections of a day to be distinguishable, which is a different need — so the seam is now
     * accepted, as the price of the split, because a day broken into its legs is the picture the list beside
     * the map already draws.
     *
     * Where a line may come from is unchanged: only a leg that brought a path is painted, and a leg without
     * one is skipped while its pins are still drawn and its distance is still said in the list. Connecting
     * its ends would report a road the provider never gave — the same invention as drawing a straight line
     * where nothing was measured. The day's own path is the fallback only when no leg has one, and it is
     * itself the legs joined end to end, so the two branches cannot describe different roads.
     *
     * A leg is dropped here rather than at draw time so the loop below has one shape, and the colours only
     * advance for legs that are actually painted: a skipped leg must not spend a colour the leg after it
     * would then not get.
     */
    const paths: Array<{ colour: string; value: string }> = [];
    if (legs !== undefined && legs.length >= 2) {
      for (const leg of legs) {
        if (leg.geometry === null) continue;
        // The index is a modulo of the list's own length, so the non-null assertion is the range, not a hope.
        paths.push({ colour: LEG_COLOURS[paths.length % LEG_COLOURS.length]!, value: leg.geometry });
      }
    }
    if (paths.length === 0 && geometry !== null) {
      paths.push({ colour: "--tp-color-accent", value: geometry });
    }

    /*
     * The paths, decoded. Decoded up front so the lines and the fit share one set of points.
     *
     * The decode is guarded, and the reason is where these strings come from: they are
     * `route_snapshots.geometry`, stored verbatim from OSRM. `decodePolyline` throws a `RangeError`
     * on a string that is truncated mid-value or carries a character outside the encoding, so an
     * unguarded call would throw out of this effect and unmount the tree — the whole planner screen
     * goes blank because one stored day's path is malformed. That is a far worse failure than a
     * missing line, so the failure is treated as "no road": the pins and the leg distances are still
     * real, a line is only the *rendering* of the measurement, and dropping the rendering loses
     * nothing a user needs while crashing loses everything. Rendering the partial path is not the
     * alternative — a route that stops in the middle of nowhere reads as a data problem rather than
     * as the decode bug it is, which is the same argument `decodePolyline`'s own comment makes for
     * throwing. The catch is silent at runtime on purpose — the codebase keeps `console` out of
     * components — and this paragraph is the record that the path exists.
     *
     * This does not touch `distancesKnown`: that is the parent's prop describing the legs, which are
     * stored in a different column. A day can have measured distances and an undrawable path, and
     * conflating the two would hide real numbers.
     */
    const drawn: Array<[number, number]> = [];
    for (const path of paths) {
      let points: Array<[number, number]>;
      try {
        points = decodePolyline(path.value).map((p) => [p[0], p[1]]);
      } catch {
        points = [];
      }
      /*
       * Fewer than two points cannot draw a segment — an empty string decodes to an empty array — and
       * drawing nothing and fitting the pins is the honest rendering of a path with no extent.
       */
      if (points.length < 2) continue;
      L.polyline(points, {
        /*
         * The colour is read from the token layer because an SVG stroke attribute cannot take `var(...)`;
         * weight 5 over a 30px pin reads as the road beneath the pins rather than as another pin.
         */
        color: readToken(path.colour),
        weight: 5,
        opacity: 0.85,
        lineJoin: "round",
        lineCap: "round",
      }).addTo(layer);
      drawn.push(...points);
    }

    for (const stop of stops) {
      const tooltipText = stop.timeLabel ? `${stop.name} · ${stop.timeLabel}` : stop.name;
      const marker = L.marker([stop.lat, stop.lon], {
        title: stop.name,
        icon: L.divIcon({
          className: "",
          // The number inside the pin is the parent's `position`, not an index — it must match the
          // timeline row beside the map, which is the whole reason the pin carries a number.
          html:
            stop.marker !== undefined && !/^\d+$/.test(stop.marker)
              ? `<div class="route-pin route-pin--anchor">${stop.marker}</div>`
              : `<div class="route-pin">${stop.marker ?? stop.position}</div>`,
          iconSize: [30, 30],
          iconAnchor: [15, 15],
        }),
      })
        .bindPopup(stop.name)
        .bindTooltip(tooltipText, {
          direction: "top",
          offset: [0, -16],
          className: "route-pin__tooltip",
        })
        .addTo(layer);

      marker.on("mouseover", () => {
        onHoverPosition?.(stop.position);
        marker.openTooltip();
      });
      marker.on("mouseout", () => {
        onHoverPosition?.(null);
        /*
         * The highlighted pin keeps its tooltip open — the highlight effect owns it — and this handler must
         * not close it. Reading `highlightedPosition` here would put that prop in this redraw's dependencies,
         * and a highlight change would then clear the layer and refit the day on every hover, which the
         * highlight effect's own comment forbids. The pin's class is the state, written by that effect.
         */
        const pin = marker.getElement()?.querySelector<HTMLElement>(".route-pin");
        if (pin?.classList.contains("route-pin--highlighted") !== true) {
          marker.closeTooltip();
        }
      });
      marker.on("click", () => {
        onSelectPosition?.(stop.position);
      });

      pinsRef.current.set(stop.position, marker);
    }

    // Fit to the day, at most once per prop change. With a drawn road — one line or several legs — its
    // bounds are the right frame: a route can wander outside the convex hull of the stops, and fitting the
    // stops would crop it. Without a road, the pins are all there is to frame.
    if (drawn.length >= 2) {
      map.fitBounds(L.latLngBounds(drawn), { padding: [30, 30], maxZoom: ROAD_FIT_MAX_ZOOM });
    } else if (stops.length === 1) {
      const only = stops[0];
      if (only !== undefined) map.setView([only.lat, only.lon], SINGLE_STOP_ZOOM);
    } else if (stops.length > 1) {
      map.fitBounds(
        L.latLngBounds(stops.map((stop) => [stop.lat, stop.lon] as [number, number])),
        { padding: [30, 30], maxZoom: STOP_FIT_MAX_ZOOM },
      );
    }
  }, [loaded, stops, geometry, legs, onHoverPosition, onSelectPosition]);

  /**
   * Keep map container dimensions aligned when layout changes or day updates.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!loaded || map === null) return;
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 150);
    return () => clearTimeout(timer);
  }, [loaded, stops, geometry]);

  /**
   * The highlight, painted onto the pins already on the map.
   *
   * Its own effect rather than part of the redraw above, and that is the whole point: the redraw clears the
   * layer and refits the view, so folding the highlight into it would make every tap on a timeline row
   * re-zoom the map — the opposite of the feature, which is to show where a stop already on the map is.
   *
   * It pans, but only when the pin is outside the current view. A pin already on screen does not move: a map
   * that shifts under a tap the user made to look *more* carefully is working against them.
   */
  useEffect(() => {
    const map = mapRef.current;
    const wanted = highlightedPosition ?? null;
    for (const [position, marker] of pinsRef.current) {
      const element = marker.getElement();
      const pin = element?.querySelector<HTMLElement>(".route-pin");
      if (pin === null || pin === undefined) continue;
      const on = position === wanted;
      pin.classList.toggle("route-pin--highlighted", on);
      pin.classList.toggle("route-pin--glow", on);
      if (on) {
        marker.openTooltip();
        if (map !== null) {
          const at = marker.getLatLng();
          if (!map.getBounds().contains(at)) map.panTo(at);
        }
      } else {
        marker.closeTooltip();
      }
    }
  }, [highlightedPosition, stops, loaded]);

  return (
    /*
     * No state modifier on the wrapper: the note below already says the route could not be drawn, and a
     * `route-map--unknown` here was a class with no stylesheet rule behind it — a hook nothing hung on.
     * The modifier that does carry the state is the note's own `route-map__unknown`.
     */
    <div className="route-map">
      {/*
        The ref is on this div, and Leaflet owns its children; React renders none inside. The empty
        div is what guarantees a fresh container on every mount — the reused-container failure
        `PlaceMap` documented ("Map container is already initialized").
      */}
      <div className="route-map__map" ref={containerRef} />
      {!distancesKnown && routingPending !== true && (
        <p className="route-map__unknown">{copy.planner.routingUnavailable}</p>
      )}
    </div>
  );
}
